"use client";

import { useMemo } from "react";
import { useMCPCredentials } from "@/components/MCP/mcpCredentials";
import { humanizeToolName } from "@/components/MCP/MCPToolRow";

export interface McpPrefixMeta {
  key: string;
  iconUrl: string;
  integrationName: string;
}

/** Mirror of ``spotflow.integrations.mcp.client.tool_prefix_for`` — derives
 *  the runtime tool-name prefix that pydantic-ai uses to namespace MCP tools.
 *  For catalog-backed servers it's the catalog key (``linear``); for custom
 *  servers it's ``custom_<sha256-prefix>`` so each server gets a unique
 *  prefix without leaking the URL. */
function toolPrefixFor(
  catalogKey: string | null,
  serverUrl: string | null
): string {
  let label: string | null = catalogKey;
  if (!label && serverUrl) {
    try {
      label = new URL(serverUrl).hostname || null;
    } catch {
      label = null;
    }
  }
  if (!label) label = "mcp";
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

/** Map of runtime tool-prefix → integration metadata for the workspace's
 *  connected MCP servers. Drives both icon rendering and human-readable
 *  tool labels in the chat. Custom MCPs each get their own prefix
 *  (``custom_<hash>``) keyed off the credential's stored catalog_key. */
export function useMcpPrefixMeta(): Map<string, McpPrefixMeta> {
  const { credentials, mcpIntegrations } = useMCPCredentials();
  return useMemo(() => {
    const map = new Map<string, McpPrefixMeta>();
    // Catalog-backed MCP integrations carry their icon via ``integration.image``
    // (sourced from the backend MCP catalog), so seed every one — this is how
    // the in-process Noxus server (``noxus_*`` tools, no workspace credential)
    // gets the Noxus logo without a hardcoded URL. A connected credential with
    // the same prefix overrides below.
    for (const integ of mcpIntegrations) {
      map.set(integ.prefix, {
        key: integ.prefix,
        iconUrl: integ.image,
        integrationName: integ.integrationName,
      });
    }
    for (const c of credentials) {
      const prefix = toolPrefixFor(c.catalog_key, c.server_url);
      if (!prefix) continue;
      // Custom MCPs share `integration_name="Custom MCP"`, so the user's
      // chosen credential label is more useful for the chat title.
      const integrationName =
        c.integration_type === "mcp_custom"
          ? c.display_name || c.integration_name
          : c.integration_name;
      map.set(prefix, {
        key: prefix,
        iconUrl: c.integration_image,
        integrationName,
      });
    }
    return map;
  }, [credentials, mcpIntegrations]);
}

export interface McpToolMatch {
  meta: McpPrefixMeta;
  toolName: string;
  toolLabel: string;
}

/** Match the longest prefix so keys like ``google_drive`` win over
 *  ``google`` when both happen to be connected. */
export function matchMcpTool(
  toolType: string,
  prefixes: Map<string, McpPrefixMeta>
): McpToolMatch | null {
  const name = toolType.replace(/^tool-/, "");
  let best: { meta: McpPrefixMeta; rest: string } | null = null;
  for (const [key, meta] of prefixes) {
    if (!name.startsWith(`${key}_`)) continue;
    const rest = name.slice(key.length + 1);
    if (!rest) continue;
    if (!best || key.length > best.meta.key.length) {
      best = { meta, rest };
    }
  }
  if (!best) return null;
  return {
    meta: best.meta,
    toolName: best.rest,
    toolLabel: humanizeToolName(best.rest),
  };
}
